/**
 * updateSubscriptionV2（商品一括変更 API）へ送る JSON を組み立てる。
 *
 * 送信直前に getSubscription で取得した **最新の契約ライン**（freshSubscription）を渡すと、
 * 別タブで既に追加済みの商品に対する「create」差分を「changeLines（数量更新）」に寄せ、
 * 二重追加（addLines の重複）を防ぐ。
 * create の newQuantity はカート上「0 → 目標本数」なので、既存行に寄せるときはサーバ数量に足さず
 * その値を契約上の最終数量として用いる（別タブ保存後の stale create で加算されないようにする）。
 */

import { Products } from '../../api/graphql/products/types';
import { SubscriptionApiType, SubscriptionLineApiType } from '../../types/api/SubscriptionApiType';
import type { DiffState, Product } from '../../hooks/use-product-cart';
import { splitDiffByType } from './productCart';
import { extractProductId } from './utils';

/** updateSubscriptionV2 の POST 本文と同じプロパティ名・構造 */
export type UpdateSubscriptionV2PostBody = {
  subscriptionContractId: string;
  addLines: {
    variantId: string;
    quantity: number;
    sellingPlanId: string;
  }[];
  changeLines: {
    lineId: string;
    quantity: number;
  }[];
  removeLines: {
    lineId: string;
  }[];
};

export type BuildUpdateSubscriptionV2Result = {
  postBody: UpdateSubscriptionV2PostBody;
  /** 変更可能商品 API（getAlternativeProducts）で確認が必要な create のみ。既存ラインに寄せた分は含まない */
  createsForAlternativeVerify: Product[];
};

/** Shopify の GID や素の ID の表記差を吸収して比較する */
export function shopifyIdsEqual(a: string, b: string): boolean {
  const tail = (s: string) => (s.includes('/') ? (s.split('/').pop() ?? s) : s);
  return tail(a) === tail(b);
}

function findMatchingLine(
  lines: SubscriptionLineApiType[],
  variantId: string,
  sellingPlanId: string
): SubscriptionLineApiType | undefined {
  return lines.find(
    (line) =>
      shopifyIdsEqual(line.variantId, variantId) && shopifyIdsEqual(line.sellingPlanId, sellingPlanId)
  );
}

/**
 * splitDiffByType 済みの create / update / remove から、POST 用の JSON を組み立てる。
 * freshLines を渡すと、create のうち既に契約に載っている variant＋販売プランの組は changeLines に回す。
 */
export function buildUpdateSubscriptionV2PostBody(
  subscriptionForContractId: SubscriptionApiType,
  products: Products,
  create: Product[],
  update: Product[],
  remove: Product[],
  freshLines: SubscriptionLineApiType[] | null
): BuildUpdateSubscriptionV2Result {
  const addLines: UpdateSubscriptionV2PostBody['addLines'] = [];
  /** lineId -> 最終数量 */
  const changeByLineId = new Map<string, number>();
  const createsForAlternativeVerify: Product[] = [];

  for (const p of update) {
    if (!p.lineId) continue;
    changeByLineId.set(p.lineId, p.newQuantity);
  }

  for (const p of create) {
    const productKey = extractProductId(p.productId) ?? p.productId;
    const shopifyProduct = products[productKey];
    if (!shopifyProduct) continue;
    if (!p.sellingPlanId) continue;

    const variantId = shopifyProduct.variantId;
    const matched =
      freshLines && findMatchingLine(freshLines, variantId, p.sellingPlanId);

    if (matched) {
      // create の newQuantity は「その行に欲しい本数」の絶対値（useProductCart の origin===0 由来）。
      // サーバの matched.quantity に足すと、別タブで既に増えた数量にローカル目標が加算されてしまう。
      changeByLineId.set(matched.lineId, p.newQuantity);
    } else {
      addLines.push({
        variantId,
        quantity: p.newQuantity,
        sellingPlanId: p.sellingPlanId,
      });
      createsForAlternativeVerify.push(p);
    }
  }

  const changeLines: UpdateSubscriptionV2PostBody['changeLines'] = [];
  changeByLineId.forEach((quantity, lineId) => {
    changeLines.push({ lineId, quantity });
  });

  const removeLines: UpdateSubscriptionV2PostBody['removeLines'] = [];
  for (const p of remove) {
    if (!p.lineId) continue;
    removeLines.push({
      lineId: p.lineId,
    });
  }

  const postBody: UpdateSubscriptionV2PostBody = {
    subscriptionContractId: subscriptionForContractId.subscriptionContractId,
    addLines,
    changeLines,
    removeLines,
  };

  return { postBody, createsForAlternativeVerify };
}

/**
 * 次回お届けの diff から、POST 用の JSON を組み立てる。
 * @param freshSubscription 送信直前に getSubscription した結果（ラインが Stale な状態と突き合わせる）
 */
export function buildUpdateSubscriptionV2PostBodyFromDiff(
  products: Products,
  diff: DiffState,
  freshSubscription: SubscriptionApiType
): BuildUpdateSubscriptionV2Result {
  const { create, update, remove } = splitDiffByType(diff);
  const freshLines = freshSubscription.subscriptionLines ?? null;
  return buildUpdateSubscriptionV2PostBody(
    freshSubscription,
    products,
    create,
    update,
    remove,
    freshLines
  );
}

/** addLines / changeLines / removeLines がすべて空なら true（POST しても意味がないとき） */
export function isEmptyUpdateSubscriptionV2PostBody(body: UpdateSubscriptionV2PostBody): boolean {
  return (
    body.addLines.length === 0 &&
    body.changeLines.length === 0 &&
    body.removeLines.length === 0
  );
}
